import { memo, ReactNode } from 'react';
import { Breadcrumbs } from '../breadcrumbs/Breadcrumbs';
import styles from './Header.module.scss';
import ItemCount from '../item-count/ItemCount';
import classNames from 'classnames';

export type Props = {
  heading: string;
  subHeading?: string;
  alternativeSearchSuggestions?: ReactNode;
  correctedSearchTerm?: ReactNode;
  numberOfResultsPosition?: 'left' | 'right' | 'hide';
  shouldShowNumberOfResults?: boolean;
  totalNumberOfResults?: number;
};

export const Header = memo(
  ({
    heading,
    subHeading,
    alternativeSearchSuggestions,
    correctedSearchTerm,
    numberOfResultsPosition = 'left',
    shouldShowNumberOfResults = true,
    totalNumberOfResults = 0,
  }: Props) => {
    const totalNumberOfResultsDisplay =
      shouldShowNumberOfResults &&
      Number.isInteger(totalNumberOfResults) &&
      totalNumberOfResults >= 0
        ? `(${totalNumberOfResults.toLocaleString('en')})`
        : '';

    return (
      <header className={styles.heading}>
        <Breadcrumbs />
        <div
          className={classNames(styles.heading__copy, {
            [styles['heading__copy-right']]:
              numberOfResultsPosition === 'right',
          })}
        >
          <h1 tabIndex={-1}>
            <span
              data-testid="heading-term"
              className={styles.heading__term}
            >{`${heading} `}</span>
            {subHeading && (
              <span
                aria-hidden="true"
                data-testid="sub-heading"
                className={styles.heading__subheading}
              >
                {subHeading}
              </span>
            )}
          </h1>
          <span
            className={classNames(styles.heading__count, {
              [styles['heading__count--hidden']]:
                numberOfResultsPosition !== 'left',
            })}
            data-testid="heading-num-results"
          >
            {totalNumberOfResultsDisplay}
          </span>
          {numberOfResultsPosition === 'right' && (
            <ItemCount
              totalNumberOfResults={totalNumberOfResults}
              className={styles.heading__countRight}
            />
          )}
        </div>
        {correctedSearchTerm}
        {alternativeSearchSuggestions}
      </header>
    );
  }
);

Header.displayName = 'Header';

export default Header;
